import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Search, Filter, UserPlus, Mail, Phone, Calendar, 
  CheckCircle, XCircle, Clock, AlertCircle, Eye, FileText,
  ChevronDown, ChevronRight, Euro, Building2, ClipboardCheck,
  Award, Send, Download, ExternalLink, Camera, ArrowUpDown, Trash2, AlertTriangle
} from 'lucide-react';
import { getAuthPhotoUrl } from '../components/AuthImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { DossiersLotBar, SelectCheckbox } from '../components/DossiersLotBar';
import { PaiementPictos } from '../components/PaiementPictos';
import { PrepaToggle } from '../components/PrepaToggle';
import { DecisionCandidat } from '../components/DecisionCandidat';

const formatEuros = (m) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(m) || 0);
const testsNonPayes = (c) => (c.statut_candidat || 'inscrit') === 'inscrit' && !c.tests_payes;
const QUICK_TITLES = { prepa: 'Inscrits à la journée de préparation', unpaid: 'Candidats sans paiement des tests' };
const QUICK_COLORS = {
  red: { on: 'bg-red-600 ring-2 ring-red-300', off: 'bg-red-50', num: 'text-red-600', sub: 'text-red-50' },
  teal: { on: 'bg-teal-600 ring-2 ring-teal-300', off: 'bg-teal-50', num: 'text-teal-600', sub: 'text-teal-50' },
};
const QuickFilterCard = ({ active, onClick, count, label, color, testId }) => {
  const k = QUICK_COLORS[color];
  return (
    <Card onClick={onClick} className={`cursor-pointer transition-shadow hover:shadow-md ${active ? k.on : k.off}`} data-testid={`${testId}-filter`}>
      <CardContent className="p-4 text-center">
        <p className={`text-2xl font-bold ${active ? 'text-white' : k.num}`} data-testid={`${testId}-count`}>{count}</p>
        <p className={`text-sm ${active ? k.sub : 'text-slate-500'}`}>{active ? `${label} · filtre actif` : label}</p>
      </CardContent>
    </Card>
  );
};

const GestionCandidats = ({ user }) => {
  const navigate = useNavigate();
  const [candidats, setCandidats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('alpha'); // 'alpha' ou 'recent'
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  const [showTestsModal, setShowTestsModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0);
  const [tarifs, setTarifs] = useState({ tests_selection: 80, journee_preparation: 120 });
  const [selected, setSelected] = useState([]);
  const [quickFilter, setQuickFilter] = useState(null);
  const toggleQuick = (key) => setQuickFilter(v => (v === key ? null : key));
  const canBulk = ['centre', 'responsable_pedagogique', 'direction'].includes(user?.role);
  const toggleSelected = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  
  // États pour les résultats des tests
  const [testsResults, setTestsResults] = useState({
    resultat_epreuves: null,  // Résultat global des épreuves VNM/Jet/Réglementation
    resultat_global: null,     // Test du centre de formation
    centre_externe: '',
    date_tests_externe: '',
    commentaires: ''
  });

  useEffect(() => {
    loadCandidats();
    loadTarifs();
  }, []);

  const loadCandidats = async () => {
    try {
      const response = await axios.get('/candidats/liste');
      setCandidats(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des candidats');
    } finally {
      setLoading(false);
    }
  };

  const loadTarifs = async () => {
    try {
      const response = await axios.get('/candidats/tarifs');
      setTarifs(response.data);
    } catch (error) {
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'inscrit':
        return <Badge className="bg-blue-100 text-blue-700">Inscrit</Badge>;
      case 'tests_payes':
        return <Badge className="bg-amber-100 text-amber-700">Tests payés</Badge>;
      case 'tests_passes':
        return <Badge className="bg-purple-100 text-purple-700">Tests passés</Badge>;
      case 'accepte':
        return <Badge className="bg-green-100 text-green-700">Accepté</Badge>;
      case 'refuse':
        return <Badge className="bg-red-100 text-red-700">Refusé</Badge>;
      case 'abandon':
        return <Badge className="bg-slate-200 text-slate-700">Abandon</Badge>;
      case 'en_formation':
        return <Badge className="bg-emerald-100 text-emerald-700">En formation</Badge>;
      default:
        return <Badge variant="outline">{statut || 'Inconnu'}</Badge>;
    }
  };

  const getEpreuveBadge = (resultat) => {
    if (resultat === 'reussi') return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (resultat === 'echec') return <XCircle className="h-5 w-5 text-red-500" />;
    if (resultat === 'externe') return <ExternalLink className="h-5 w-5 text-blue-500" />;
    return <Clock className="h-5 w-5 text-slate-300" />;
  };

  const filteredCandidats = candidats.filter(c => {
    if (quickFilter === 'prepa' && !c.fait_prepa) return false;
    if (quickFilter === 'unpaid' && !testsNonPayes(c)) return false;
    const matchesSearch = 
      c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }).sort((a, b) => {
    if (sortMode === 'recent') {
      const dateA = a.created_at || a.date_inscription || '';
      const dateB = b.created_at || b.date_inscription || '';
      return dateB.localeCompare(dateA);
    }
    return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
  });

  const handleSaveTestResults = async () => {
    if (!selectedCandidat) return;
    
    try {
      await axios.put(`/candidats/${selectedCandidat.user_id}/tests`, testsResults);
      toast.success('Résultats enregistrés — envoyez-les au candidat depuis sa fiche');
      setShowTestsModal(false);
      loadCandidats();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const openTestsModal = (candidat) => {
    setSelectedCandidat(candidat);
    // Charger les résultats existants si présents
    const profil = candidat.profil || {};
    const tests = profil.tests_selection || {};
    setTestsResults({
      resultat_epreuves: tests.resultat_epreuves || null,
      resultat_global: tests.resultat_global === 'recu' ? 'admis' : tests.resultat_global === 'ajourne' ? 'non_admis' : (tests.resultat_global || null),
      centre_externe: tests.centre_externe || '',
      date_tests_externe: tests.date_tests_externe || '',
      commentaires: tests.commentaires || ''
    });
    setShowTestsModal(true);
  };

  const stats = {
    total: candidats.length,
    inscrits: candidats.filter(c => c.statut_candidat === 'inscrit').length,
    testsPaies: candidats.filter(c => c.statut_candidat === 'tests_payes').length,
    testsPasses: candidats.filter(c => c.statut_candidat === 'tests_passes').length,
    acceptes: candidats.filter(c => c.statut_candidat === 'accepte').length,
    prepa: candidats.filter(c => c.fait_prepa).length,
    unpaid: candidats.filter(testsNonPayes).length,
  };

  const handleDeleteCandidat = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/candidats/${deleteTarget.user_id}`);
      toast.success(`${deleteTarget.prenom} ${deleteTarget.nom} a ete supprime`);
      setCandidats(prev => prev.filter(c => c.user_id !== deleteTarget.user_id));
      setDeleteTarget(null);
      setDeleteConfirmStep(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };


  const handleDownloadTrombinoscope = async () => {
    try {
      toast.info('Génération du trombinoscope en cours...');
      const response = await axios.get('/candidats/trombinoscope/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${new Date().getFullYear()}_trombinoscope_candidats.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Trombinoscope téléchargé !');
    } catch (error) {
      toast.error('Erreur lors de la génération du trombinoscope');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-candidats">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Gestion des Candidats</h1>
          <p className="text-slate-500">Tests de sélection BPJEPS Activités de Motonautisme</p>
        </div>
        <Button onClick={handleDownloadTrombinoscope} variant="outline" className="border-[#8DC63F] text-[#8DC63F] hover:bg-green-50" data-testid="btn-trombinoscope">
          <Camera className="h-4 w-4 mr-2" />
          Trombinoscope PDF
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0F172A]">{stats.total}</p>
            <p className="text-sm text-slate-500">Total candidats</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.inscrits}</p>
            <p className="text-sm text-slate-500">Inscrits</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.testsPaies}</p>
            <p className="text-sm text-slate-500">Tests payés</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.testsPasses}</p>
            <p className="text-sm text-slate-500">Tests passés</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.acceptes}</p>
            <p className="text-sm text-slate-500">Acceptés</p>
          </CardContent>
        </Card>
        <QuickFilterCard active={quickFilter === 'unpaid'} onClick={() => toggleQuick('unpaid')} count={stats.unpaid}
          label="Tests non payés" color="red" testId="stat-unpaid" />
        <QuickFilterCard active={quickFilter === 'prepa'} onClick={() => toggleQuick('prepa')} count={stats.prepa}
          label="Inscrits préparation" color="teal" testId="stat-prepa" />
      </div>

      {/* Recherche + Tri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher un candidat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F] text-sm"
              data-testid="sort-candidats"
            >
              <option value="alpha">Tri A → Z</option>
              <option value="recent">Dernier inscrit en premier</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des candidats */}
      {canBulk && <DossiersLotBar source="candidats" selectedIds={selected} onClear={() => setSelected([])} />}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#8DC63F]" />
            {QUICK_TITLES[quickFilter] || 'Candidats'} ({filteredCandidats.length})
            {quickFilter && (
              <button onClick={() => setQuickFilter(null)} className="ml-2 text-xs font-normal text-slate-600 underline" data-testid="quick-filter-clear">Voir tous les candidats</button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCandidats.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun candidat trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCandidats.map((candidat) => (
                <div
                  key={candidat.user_id}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#8DC63F] transition-colors cursor-pointer"
                  onClick={() => navigate(`/fiche-candidat?id=${candidat.user_id}`)}
                  data-testid={`candidat-row-${candidat.user_id}`}
                >
                  <div className="flex items-center gap-4 mb-3 md:mb-0">
                    {canBulk && (
                      <SelectCheckbox checked={selected.includes(candidat.user_id)} onToggle={() => toggleSelected(candidat.user_id)} testId={`select-candidat-${candidat.user_id}`} />
                    )}
                    {candidat.photo_url ? (
                      <img src={getAuthPhotoUrl(candidat.photo_url)} alt={`${candidat.prenom} ${candidat.nom}`}
                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        data-testid={`candidat-photo-${candidat.user_id}`}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center ${candidat.photo_url ? 'hidden' : ''}`}>
                      <span className="text-[#8DC63F] font-bold">
                        {candidat.prenom?.[0]}{candidat.nom?.[0]}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-[#0F172A]">
                          {candidat.prenom} {candidat.nom}
                        </h3>
                        {candidat.numero_candidat && (
                          <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200" data-testid={`numero-candidat-${candidat.user_id}`}>
                            N°{candidat.numero_candidat}
                          </span>
                        )}
                        {getStatutBadge(candidat.statut_candidat)}
                        {candidat.is_new && (
                          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full animate-pulse" data-testid="badge-nouveau">
                            NOUVEAU
                          </span>
                        )}
                        {candidat.has_pending_docs && (
                          <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1" data-testid="badge-docs-pending">
                            <FileText className="h-3 w-3" />
                            {candidat.nb_pending_docs} doc(s)
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {candidat.email}
                        </span>
                        {candidat.telephone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {candidat.telephone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {candidat.centre === 'occitanie' ? 'Occitanie' : 'Atlantique'}
                        </span>
                        <span className="flex items-center gap-1" data-testid={`paiements-detail-${candidat.user_id}`}>
                          <Euro className="h-3 w-3" />
                          <span className={candidat.paiement_tests?.statut === 'paye' ? 'text-green-600' : 'text-red-500'} data-testid={`paiement-tests-${candidat.user_id}`}>
                            Tests : {candidat.paiement_tests?.statut === 'paye' ? formatEuros(candidat.paiement_tests.montant) : 'non payé'}
                          </span>
                          {/* Préparation facultative : affichée seulement si le candidat la fait */}
                          {candidat.fait_prepa && (
                            <span className={candidat.prepa_payee ? 'text-green-600' : 'text-red-500'} data-testid={`paiement-prepa-${candidat.user_id}`}>
                              <span className="text-slate-400"> · </span>
                              Préparation : {candidat.prepa_payee ? formatEuros(candidat.paiement_prepa.montant) : 'non payé'}
                            </span>
                          )}
                        </span>
                      </div>
                      {candidat.statut_candidat === 'abandon' && candidat.motif_abandon && (
                        <p className="text-xs text-slate-500 mt-1" data-testid={`motif-abandon-${candidat.user_id}`}>Motif de l'abandon : {candidat.motif_abandon}</p>
                      )}
                      <div className="md:hidden mt-2">
                        <PaiementPictos candidat={candidat} small />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <div className="hidden md:flex mr-1">
                      <PaiementPictos candidat={candidat} />
                    </div>
                    <PrepaToggle candidatId={candidat.user_id} value={candidat.fait_prepa} payee={candidat.prepa_payee} compact
                      onChange={(v) => setCandidats(prev => prev.map(c => (c.user_id === candidat.user_id ? { ...c, fait_prepa: v } : c)))} />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openTestsModal(candidat)}
                      data-testid={`btn-tests-${candidat.user_id}`}
                    >
                      <ClipboardCheck className="h-4 w-4 mr-1" />
                      Tests
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => { setDeleteTarget(candidat); setDeleteConfirmStep(1); }}
                      data-testid={`btn-delete-${candidat.user_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>

                    <DecisionCandidat candidatId={candidat.user_id} statut={candidat.statut_candidat} motif={candidat.motif_abandon}
                      onChanged={(d) => setCandidats(prev => prev.map(c => (c.user_id === candidat.user_id ? { ...c, statut_candidat: d.statut_candidat, motif_abandon: d.motif_abandon } : c)))} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Tests de Sélection */}
      <Dialog open={showTestsModal} onOpenChange={setShowTestsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tests de Sélection</DialogTitle>
            <DialogDescription>
              {selectedCandidat?.prenom} {selectedCandidat?.nom}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Section 1 : Épreuves techniques (résultat global) */}
            <div className="space-y-4">
              <h4 className="font-semibold text-[#0F172A] border-b pb-2">
                Épreuves techniques (VNM / Jet à bras / Réglementation)
              </h4>
              <p className="text-sm text-slate-500">
                Résultat global des 3 épreuves techniques
              </p>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setTestsResults({...testsResults, resultat_epreuves: 'reussi'})}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                    testsResults.resultat_epreuves === 'reussi'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                >
                  <CheckCircle className="h-6 w-6 mx-auto mb-2" />
                  <p className="font-medium">RÉUSSITE</p>
                </button>
                <button
                  onClick={() => setTestsResults({...testsResults, resultat_epreuves: 'ajourne'})}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                    testsResults.resultat_epreuves === 'ajourne'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 hover:border-red-300'
                  }`}
                >
                  <XCircle className="h-6 w-6 mx-auto mb-2" />
                  <p className="font-medium">AJOURNÉ</p>
                </button>
                <button
                  onClick={() => setTestsResults({...testsResults, resultat_epreuves: 'externe'})}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                    testsResults.resultat_epreuves === 'externe'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <ExternalLink className="h-6 w-6 mx-auto mb-2" />
                  <p className="font-medium">EXTERNE</p>
                </button>
              </div>
            </div>

            {/* Si épreuves passées en externe */}
            {testsResults.resultat_epreuves === 'externe' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-700">Épreuves passées en externe</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Centre externe</Label>
                    <Input
                      value={testsResults.centre_externe}
                      onChange={(e) => setTestsResults({...testsResults, centre_externe: e.target.value})}
                      placeholder="Nom du centre"
                    />
                  </div>
                  <div>
                    <Label>Date des tests</Label>
                    <Input
                      type="date"
                      value={testsResults.date_tests_externe}
                      onChange={(e) => setTestsResults({...testsResults, date_tests_externe: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section 2 : Test du centre de formation */}
            <div className="space-y-4">
              <h4 className="font-semibold text-[#0F172A] border-b pb-2">
                Test du centre de formation
              </h4>
              <p className="text-sm text-slate-500">
                Décision finale après entretien avec le jury
              </p>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setTestsResults({...testsResults, resultat_global: 'admis'})}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                    testsResults.resultat_global === 'admis'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                >
                  <CheckCircle className="h-6 w-6 mx-auto mb-2" />
                  <p className="font-medium">ADMIS</p>
                </button>
                <button
                  onClick={() => setTestsResults({...testsResults, resultat_global: 'non_admis'})}
                  className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                    testsResults.resultat_global === 'non_admis'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 hover:border-red-300'
                  }`}
                >
                  <XCircle className="h-6 w-6 mx-auto mb-2" />
                  <p className="font-medium">NON ADMIS</p>
                </button>
              </div>
            </div>

            {/* Commentaires */}
            <div>
              <Label>Commentaires (optionnel)</Label>
              <textarea
                value={testsResults.commentaires}
                onChange={(e) => setTestsResults({...testsResults, commentaires: e.target.value})}
                className="w-full mt-1 p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                rows={3}
                placeholder="Notes sur le candidat..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestsModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveTestResults}
              className="bg-[#8DC63F] hover:bg-[#7db336]"
            >
              Enregistrer les résultats
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de suppression candidat - Double confirmation */}
      <Dialog open={deleteConfirmStep > 0} onOpenChange={(open) => { if (!open) { setDeleteConfirmStep(0); setDeleteTarget(null); } }}>
        <DialogContent className="max-w-md">
          {deleteConfirmStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Supprimer ce candidat ?
                </DialogTitle>
                <DialogDescription>
                  Vous allez supprimer <strong>{deleteTarget?.prenom} {deleteTarget?.nom}</strong> et toutes ses donnees associees (documents, profil, prospect).
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setDeleteConfirmStep(0); setDeleteTarget(null); }}>
                  Annuler
                </Button>
                <Button variant="destructive" onClick={() => setDeleteConfirmStep(2)} data-testid="delete-confirm-step1">
                  Oui, supprimer
                </Button>
              </DialogFooter>
            </>
          )}
          {deleteConfirmStep === 2 && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Confirmation finale
                </DialogTitle>
                <DialogDescription>
                  Cette action est <strong>irreversible</strong>. Toutes les donnees de <strong>{deleteTarget?.prenom} {deleteTarget?.nom}</strong> seront definitivement supprimees.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setDeleteConfirmStep(0); setDeleteTarget(null); }}>
                  Non, annuler
                </Button>
                <Button variant="destructive" onClick={handleDeleteCandidat} data-testid="delete-confirm-step2">
                  Supprimer definitivement
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default GestionCandidats;
